/**
 * Modal de mapa de um endereço. Abre sobre a lista de endereços e mostra o ponto no mapa —
 * ocupa a tela toda no celular e ~metade dela (50vw × 85vh, centralizado) no desktop.
 *
 * Com `VITE_GOOGLE_MAPS_API_KEY`, renderiza o mapa interativo (Maps JS) — base do rastreamento
 * futuro. Sem a chave (ou se o Maps JS falhar), cai num `iframe` do Google no modo `output=embed`
 * (keyless): a `query` é a coordenada quando o endereço já foi georreferenciado (enriquecimento do
 * CEP), ou o texto do endereço quando não — o próprio mapa geocodifica. Nunca fica em branco.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { ContatoEnderecoCad } from '@/lib/api';
import { MapaInterativo } from './MapaInterativo';

type Endereco = ContatoEnderecoCad['endereco'];

const CHAVE_MAPS = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

function textoEndereco(e: Endereco): string {
  return [e.logradouro, e.numero, e.bairro, e.cidade, e.uf, e.cep]
    .filter(Boolean)
    .join(', ');
}

/** Coordenada quando houver (mais preciso); senão o texto do endereço, que o mapa geocodifica. */
function consultaMapa(e: Endereco): string {
  if (e.latitude && e.longitude) return `${e.latitude},${e.longitude}`;
  return textoEndereco(e);
}

/** Coordenadas numéricas do endereço, ou null — o Maps JS precisa de `number`, não da string. */
function coords(e: Endereco): { lat: number; lng: number } | null {
  if (e.latitude == null || e.longitude == null) return null;
  const lat = Number(e.latitude);
  const lng = Number(e.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

export function ModalMapa({
  endereco,
  onFechar,
}: {
  endereco: Endereco | null;
  onFechar: () => void;
}) {
  const aberto = endereco !== null;
  const consulta = endereco ? consultaMapa(endereco) : '';
  const q = encodeURIComponent(consulta);
  const srcEmbed = `https://maps.google.com/maps?q=${q}&z=16&output=embed`;
  const srcExterno = `https://www.google.com/maps/search/?api=1&query=${q}`;

  // Cada endereço recomeça no Maps JS (quando há chave); se ele falhar, este alvo cai no iframe.
  const [googleFalhou, setGoogleFalhou] = useState(false);
  useEffect(() => setGoogleFalhou(false), [consulta]);
  const aoFalharGoogle = useCallback(() => setGoogleFalhou(true), []);

  const ponto = endereco ? coords(endereco) : null;
  const usarGoogle = Boolean(CHAVE_MAPS) && !googleFalhou;

  return (
    <Dialog.Root open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm" />
        <Dialog.Content
          className={
            'fixed z-[var(--z-modal)] flex flex-col overflow-hidden bg-surface ' +
            // Celular: tela cheia. Desktop (md+): metade da largura, centralizado.
            'inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:h-[85vh] md:w-[50vw] ' +
            'md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:border md:border-border md:shadow-[var(--shadow-lg)]'
          }
        >
          <header className="flex items-center justify-between gap-3 border-b border-border p-4">
            <div className="min-w-0">
              <Dialog.Title className="tipo-subtitulo truncate text-text">
                {endereco ? textoEndereco(endereco) : 'Mapa'}
              </Dialog.Title>
              <Dialog.Description className="sr-only">
                Mapa do endereço selecionado.
              </Dialog.Description>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <a
                href={srcExterno}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Abrir no Google Maps"
                title="Abrir no Google Maps"
                className="rounded-md p-2 text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
              >
                <ExternalLink className="size-4" />
              </a>
              <Dialog.Close
                aria-label="Fechar"
                className="rounded-md p-2 text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
              >
                <X className="size-4" />
              </Dialog.Close>
            </div>
          </header>

          <div className="relative flex-1 bg-surface-2">
            {aberto && usarGoogle && CHAVE_MAPS && (
              <MapaInterativo
                key={consulta}
                apiKey={CHAVE_MAPS}
                latitude={ponto?.lat}
                longitude={ponto?.lng}
                consultaTexto={consulta}
                titulo={textoEndereco(endereco)}
                onFalhar={aoFalharGoogle}
              />
            )}
            {aberto && !usarGoogle && (
              <iframe
                key={consulta}
                title="Mapa do endereço"
                src={srcEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 size-full border-0"
              />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
